import { Injectable, Injector } from '@angular/core';
import {
  HttpContextToken,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HTTP_INTERCEPTORS,
} from '@angular/common/http';
import { Observable } from 'rxjs';
import { finalize, tap } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { TokenService } from './token.service';

/**
 * Set on an HttpRequest's context (via `new HttpContext().set(EXPIRE_SESSION_ON_401, true)`) to
 * let a 401 on that request end the session.
 *
 * Opt-in, and deliberately so. A 401 only means "the auth cookie is gone" on an endpoint that does
 * nothing but assert the cookie - Authenticate/heartbeat is the one we have. Everywhere else the
 * API also returns 401 for authorization failures: ~25 controller actions answer a failed
 * HasOrganizationAccess with Unauthorized(), and Startup maps UnauthorizedAccessException to 401
 * as well. Those mean "not your row", not "not logged in", and destroying the session over one
 * logged the user out for clicking an inactive org.
 *
 * So a request that does not set this leaves its 401 to the caller, which is where the handling
 * already lives - see OrganizationNavigationGuard, which catches 401/403 and shows
 * Unauthorized.OrganizationNotInScope. What it does do is ask the heartbeat - see
 * AuthInterceptor.checkSession.
 */
export const EXPIRE_SESSION_ON_401 = new HttpContextToken<boolean>(() => false);

/** Must match TokenRefreshService; the refresh call's refusals are its own to handle. */
const REFRESH_ENDPOINT = 'Authenticate/refresh';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  /** True while a session check is waiting on its heartbeat, so a burst of refusals sends one. */
  private checkingSession = false;

  // AuthService depends on HttpClient, so injecting it here would create a cyclic dependency
  // while the interceptor chain is being built. Resolve it lazily, once a 401 has happened.
  constructor(
    private injector: Injector,
    private tokenService: TokenService,
  ) {}

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      tap({
        error: (err: unknown) => {
          if (!(err instanceof HttpErrorResponse)) return;

          if (request.context.get(EXPIRE_SESSION_ON_401)) {
            // The auth cookie is gone or past its absolute cap - there is nothing to refresh.
            if (err.status === 401) this.injector.get(AuthService).expireSession();
            return;
          }

          if (err.status === 401 || err.status === 403) this.checkSession(request);
        },
      })
    );
  }

  /**
   * Asks the heartbeat whether a refusal on an ordinary request means the session is over.
   *
   * The refusal alone cannot say: the same status is this API's answer to "not your row" (see
   * EXPIRE_SESSION_ON_401). But a session revoked elsewhere - a sign-out on another device whose
   * UserLogout broadcast never reached this tab - refuses every request this tab makes, and until
   * this the tab sat on cached data, failing quietly, until its next timed heartbeat up to five
   * minutes later. The heartbeat is the one request whose 401 does mean the session is gone, so it
   * decides: a 401 there ends the session through the branch above, a 200 means this was a scope
   * refusal and nothing happens.
   *
   * Only for a tab holding a token that is still in date. With none there is no session to check -
   * a failed sign-in, or the echo logout a tab sends on its way out. An expired one is
   * JwtInterceptor's to refresh. The refresh call itself is skipped because TokenRefreshService
   * clears the token on any failure of its own.
   */
  private checkSession(request: HttpRequest<unknown>): void {
    if (
      this.checkingSession ||
      request.url.includes(REFRESH_ENDPOINT) ||
      this.tokenService.isExpired()
    ) {
      return;
    }

    this.checkingSession = true;
    this.injector
      .get(AuthService)
      .heartbeat()
      .pipe(finalize(() => (this.checkingSession = false)))
      // Errors are handled in intercept: a 401 expires the session; anything else is a fault and
      // proves nothing, the same way LoggedInGuard treats it.
      .subscribe({ error: () => undefined });
  }
}

export const authInterceptorProvider = {
  provide: HTTP_INTERCEPTORS,
  useClass: AuthInterceptor,
  multi: true,
};
